/**
 * Data layer for the COMPANY panel's Perks section, backed by the company/v1
 * perks-hub endpoints (token-scoped to the company; the B2B/Members toggle is
 * appended by api/companyApi.js on every call).
 *
 * Page-facing shapes come from the shared adapters in
 * container/PerksHub/perksHubApi.js, so a future super-admin version of these
 * dashboards reads the payload identically.
 *
 * Every fetcher takes an optional `department` so the section's filter can
 * narrow the whole page to one department. Availability is the exception — it
 * describes the company's contract, not a slice of its people.
 */

import {
  getCompanyPerksAvailability,
  getCompanyPerksOverview,
  getCompanyInstantSupportDashboard,
  getCompanySalarySacrificeDashboard,
  getCompanyHealthSchemeDashboard,
  getCompanyCustomPerksDashboard,
  getCompanyPerksUsers,
  getCompanyPerksUserDetail
} from '../../../../api/companyApi';
import { unwrap } from '../../../SleepHub/sleepHubApi';
import {
  adaptAvailability,
  adaptOverview,
  adaptInstantSupport,
  adaptSchemeDashboard,
  adaptCustomPerks,
  adaptUserDetail
} from '../../../PerksHub/perksHubApi';

/**
 * Which programmes this company has. A failure here must not read as "you have
 * everything" — the layout would then render tabs whose endpoints 403 — so the
 * fallback is all-off with `enabled: false`.
 */
export const fetchPerksAvailability = async () => {
  try {
    return adaptAvailability(unwrap(await getCompanyPerksAvailability()));
  } catch (error) {
    console.error('fetchPerksAvailability error:', error);
    return adaptAvailability(null);
  }
};

export const fetchPerksOverview = async (department = '') => {
  try {
    return adaptOverview(unwrap(await getCompanyPerksOverview(department)));
  } catch (error) {
    console.error('fetchPerksOverview error:', error);
    return null;
  }
};

export const fetchInstantSupport = async (department = '') => {
  try {
    return adaptInstantSupport(unwrap(await getCompanyInstantSupportDashboard(department)));
  } catch (error) {
    console.error('fetchInstantSupport error:', error);
    return null;
  }
};

export const fetchSalarySacrifice = async (department = '') => {
  try {
    return adaptSchemeDashboard(unwrap(await getCompanySalarySacrificeDashboard(department)));
  } catch (error) {
    console.error('fetchSalarySacrifice error:', error);
    return null;
  }
};

export const fetchHealthScheme = async (department = '') => {
  try {
    return adaptSchemeDashboard(unwrap(await getCompanyHealthSchemeDashboard(department)));
  } catch (error) {
    console.error('fetchHealthScheme error:', error);
    return null;
  }
};

export const fetchCustomPerks = async (department = '') => {
  try {
    return adaptCustomPerks(unwrap(await getCompanyCustomPerksDashboard(department)));
  } catch (error) {
    console.error('fetchCustomPerks error:', error);
    return null;
  }
};

// Matches the PerksUsersTable fetcher contract.
export const fetchPerksUsers = async ({
  page = 1,
  perPage = 10,
  search = '',
  department = ''
} = {}) => {
  try {
    const data = unwrap(await getCompanyPerksUsers(page, perPage, search, department));
    return { data: data.data || [], totalCount: data.totalCount || 0 };
  } catch (error) {
    console.error('fetchPerksUsers error:', error);
    return { data: [], totalCount: 0 };
  }
};

/**
 * One employee's/member's perks profile. The backend 404s any user outside
 * this company, so no scoping is needed here.
 */
export const fetchPerksUserDetail = async (userId) => {
  try {
    return adaptUserDetail(unwrap(await getCompanyPerksUserDetail(userId)));
  } catch (error) {
    console.error('fetchPerksUserDetail error:', error);
    return null;
  }
};
